import { isValidDobDate, toDobIso } from '@pesto/utils/src/dateHelper';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { z } from 'zod';

import type {
  AccountOpeningApplicantInput,
  AccountOpeningCardInput,
  OpenAccountMutationMutationVariables,
  UserInput,
} from '../../../__generated__/graphql/api';

import { CardPlan, KycSource } from '../../../__generated__/graphql/api';

export const MAX_APPLICANTS = 2;

const stringSchema = (field: string) => z.string().max(255, `${field} is too long`);

const hasInitialBalance = (value: number | undefined): value is number => !!value && value > 0;

const isDob = (value: string) => /^\d{2}-\d{2}-\d{4}$/.test(value) && isValidDobDate(value);

const ssnDigits = (value: string | undefined) => value?.replace(/\D/g, '') ?? '';

const isIssuableSsn = (digits: string) =>
  !['000', '666'].includes(digits.slice(0, 3)) && digits.slice(3, 5) !== '00' && digits.slice(5) !== '0000';

export const applicantSchema = z.object({
  email: stringSchema('Email').email('Invalid email format').min(1, `Email is required`),
  first_name: stringSchema('First name').min(1, `First name is required`),
  middle_name: stringSchema('Middle name').optional(),
  last_name: stringSchema('Last name').min(1, `Last name is required`),
  phone: stringSchema('Phone').refine((val) => isPossiblePhoneNumber(val), {
    message: 'Invalid phone number',
  }),
  dob: z
    .string()
    .min(1, 'Date of birth is required')
    .refine((value) => !value || isDob(value), 'Use DD-MM-YYYY, a real date since 1900'),
  ssn: z
    .string()
    .optional()
    .refine((value) => !value || ssnDigits(value).length === 9, 'SSN must be 9 digits')
    .refine(
      (value) => !value || ssnDigits(value).length !== 9 || isIssuableSsn(ssnDigits(value)),
      'Not a valid SSN: area 000 or 666, group 00 and serial 0000 are never issued',
    ),
  address_line1: stringSchema('Address line 1').min(1, `Address line 1 is required`),
  address_line2: stringSchema('Address line 2').optional(),
  city: stringSchema('City').min(1, `City is required`),
  state: stringSchema('State').min(1, `State is required`),
  zip: stringSchema('ZIP code')
    .regex(/^\d{5}$/, 'Invalid ZIP code format')
    .min(1, `ZIP code is required`),
  card_ref: z.string().optional(),
  last_four: z.string().max(4, 'Last four must be 4 digits').optional().or(z.literal('')),
});

export type ApplicantFormData = z.infer<typeof applicantSchema>;

const addIssue = (ctx: z.RefinementCtx, path: Array<string | number>, message: string) =>
  ctx.addIssue({ code: 'custom', path, message });

export const isAttestedImport = ({ kyc, card_plan }: { kyc?: KycSource; card_plan?: CardPlan }) =>
  kyc === KycSource.Attest && card_plan === CardPlan.Import;

const addCardPairIssues = (applicant: ApplicantFormData, index: number, ctx: z.RefinementCtx) => {
  const hasCardRef = Boolean(applicant.card_ref?.trim());
  const lastFour = applicant.last_four?.trim() ?? '';

  if (lastFour && !hasCardRef) {
    addIssue(ctx, ['applicants', index, 'card_ref'], 'Card reference is required with last four');
  }

  if (hasCardRef && !/^\d{4}$/.test(lastFour)) {
    addIssue(ctx, ['applicants', index, 'last_four'], 'Last four is required (4 digits)');
  }
};

const DISTINCT_FIELDS = {
  email: { label: 'email', valueOf: (applicant: ApplicantFormData) => applicant.email.trim().toLowerCase() },
  phone: { label: 'phone', valueOf: (applicant: ApplicantFormData) => applicant.phone },
  ssn: { label: 'SSN', valueOf: (applicant: ApplicantFormData) => ssnDigits(applicant.ssn) },
  card_ref: { label: 'card reference', valueOf: (applicant: ApplicantFormData) => applicant.card_ref?.trim() ?? '' },
};

const addDistinctIssues = (applicants: ApplicantFormData[], importsCards: boolean, ctx: z.RefinementCtx) => {
  const fields = Object.entries(DISTINCT_FIELDS).filter(([field]) => importsCards || field !== 'card_ref');

  fields.forEach(([field, { label, valueOf }]) => {
    const values = applicants.map(valueOf);
    values.forEach((value, index) => {
      if (value && values.indexOf(value) < index) {
        addIssue(ctx, ['applicants', index, field], `Each applicant needs a different ${label}`);
      }
    });
  });
};

export const openAccountSchema = z
  .object({
    applicants: z
      .array(applicantSchema)
      .min(1, 'At least one applicant is required')
      .max(MAX_APPLICANTS, `At most ${MAX_APPLICANTS} applicants`),
    program_id: z.string().min(1, 'Program is required'),
    partner_account_number: stringSchema('Partner account number').min(1, 'Partner account number is required'),
    kyc: z.nativeEnum(KycSource, { errorMap: () => ({ message: 'Choose to run or skip KYC' }) }),
    attestation_reason: stringSchema('Reason').optional(),
    card_plan: z.nativeEnum(CardPlan, { errorMap: () => ({ message: 'Choose to issue or import cards' }) }),
    credit_limit: z
      .number()
      .min(1, 'Credit limit must be greater than zero')
      .refine((val) => /^[0-9]+$/.test(val.toString()), {
        message: 'Credit limit must contain only numbers',
      }),
    initial_balance: z.number().min(0, 'Opening balance cannot be negative').optional(),
    origination_date: z.string().min(1, 'Origination date is required'),
    promo_end_date: z.string().optional().or(z.literal('')),
    apr_margin: stringSchema('APR margin').min(1, 'APR margin is required'),
    initial_apr: stringSchema('Initial APR').min(1, 'Initial APR is required'),
  })
  .superRefine((data, ctx) => {
    if (data.kyc === KycSource.Attest && !data.attestation_reason?.trim()) {
      addIssue(ctx, ['attestation_reason'], 'Enter a reason for skipping KYC');
    }

    const ssnRequired = !isAttestedImport(data);
    const importsCards = data.card_plan === CardPlan.Import;

    data.applicants.forEach((applicant, index) => {
      if (ssnRequired && !applicant.ssn) {
        addIssue(ctx, ['applicants', index, 'ssn'], 'SSN is required');
      }

      if (importsCards) {
        addCardPairIssues(applicant, index, ctx);
      }
    });

    addDistinctIssues(data.applicants, importsCards, ctx);
  });

export type OpenAccountFormData = z.infer<typeof openAccountSchema>;

const toCardInput = (applicant: ApplicantFormData): AccountOpeningCardInput | undefined => {
  const cardRef = applicant.card_ref?.trim();

  return cardRef ? { cardRef, lastFour: applicant.last_four?.trim() ?? '' } : undefined;
};

const toApplicantInput = (applicant: ApplicantFormData, cardPlan: CardPlan): AccountOpeningApplicantInput => {
  const card = cardPlan === CardPlan.Import ? toCardInput(applicant) : undefined;

  return {
    email: applicant.email,
    firstName: applicant.first_name,
    middleName: applicant.middle_name || undefined,
    lastName: applicant.last_name,
    phone: applicant.phone,
    dateOfBirth: toDobIso(applicant.dob),
    ...(applicant.ssn && { ssn: ssnDigits(applicant.ssn) }),
    address: {
      line1: applicant.address_line1,
      line2: applicant.address_line2 || undefined,
      city: applicant.city,
      state: applicant.state,
      zip: applicant.zip,
    },
    ...(card && { card }),
  };
};

export const toUserInput = (applicant: ApplicantFormData): UserInput => ({
  email: applicant.email,
  firstName: applicant.first_name,
  middleName: applicant.middle_name || undefined,
  lastName: applicant.last_name,
  phone: applicant.phone,
  dob: toDobIso(applicant.dob),
  addressInput: {
    addressFirstLine: applicant.address_line1,
    addressSecondLine: applicant.address_line2 || undefined,
    city: applicant.city,
    state: applicant.state,
    zip: applicant.zip,
  },
});

export const toOpenAccountVariables = (data: OpenAccountFormData): OpenAccountMutationMutationVariables => ({
  input: {
    programId: data.program_id,
    partnerAccountNumber: data.partner_account_number,
    kyc: data.kyc,
    ...(data.kyc === KycSource.Attest && { attestationReason: data.attestation_reason?.trim() }),
    cardPlan: data.card_plan,
    applicants: data.applicants.map((applicant) => toApplicantInput(applicant, data.card_plan)),
    terms: {
      creditLimit: data.credit_limit,
      originationDate: data.origination_date,
      aprMargin: data.apr_margin,
      initialApr: data.initial_apr,
      ...(hasInitialBalance(data.initial_balance) && { initialBalance: data.initial_balance }),
      ...(data.promo_end_date && { promoEndDate: data.promo_end_date }),
    },
  },
});

export const emptyApplicant: ApplicantFormData = {
  email: '',
  first_name: '',
  middle_name: '',
  last_name: '',
  phone: '',
  dob: '',
  ssn: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip: '',
  card_ref: undefined,
  last_four: '',
};
